import {
  AHRoutineIcon,
  AHSeparator,
  AHText,
  AHTextFonts,
  Box,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { PropsWithChildren, useMemo, useState } from "react";
import { Dimensions, View } from "react-native";

import * as d from "d3-scale";

import Svg, { ClipPath, G, Rect, Text } from "react-native-svg";

import { graphql, useFragment } from "react-relay";

import { formatValue } from "arkh/common";
import {
  KPIPerWeekdayAnalytics$key,
  RoutineIcon,
} from "./__generated__/KPIPerWeekdayAnalytics.graphql";
import { useWeekdays } from "../../common/useWeekdays";
import { toClipPathUrl } from "./toClipPathUrl";

export function KPIPerWeekdayAnalytics(props: {
  kpi: KPIPerWeekdayAnalytics$key;
  sectionIdsFilter?: string[];
}) {
  const data = useFragment(
    graphql`
      fragment KPIPerWeekdayAnalytics on KPI {
        id
        title
        icon
        unit
        scheduledDays
        weekdaySectionAggregates {
          section {
            id
            title
            assignedTeam {
              icon
              color
              name
            }
          }
          results {
            weekday
            value
          }
        }
      }
    `,
    props.kpi
  );
  const theme = useTheme();

  if (data.scheduledDays.length === 1) {
    return null;
  }
  return (
    <PerWeekdayGraph
      title={data.title}
      icon={data.icon}
      unit={data.unit}
      sections={(props.sectionIdsFilter
        ? data.weekdaySectionAggregates.filter(
            (s) =>
              s?.section.id && props.sectionIdsFilter!.includes(s.section.id)
          )
        : data.weekdaySectionAggregates
      ).map((s) => ({
        id: s?.section.id!,
        color: s?.section.assignedTeam?.color ?? theme.darkerText,
        title: (s?.section.title || s?.section.assignedTeam?.name) ?? "",
        results:
          s?.results.map((r) => ({
            weekday: r.weekday,
            value: r.value,
          })) ?? [],
      }))}
    />
  );
}

function PerWeekdayGraph({
  sections,
  title,
  icon,
  unit,
}: {
  title?: string;
  icon?: RoutineIcon | null;
  unit: "CUSTOM" | "CURRENCY";
  sections: readonly {
    id: string;
    title: string;
    color: string;
    results: { readonly weekday: number; readonly value: number }[];
  }[];
}) {
  const ul = useLayoutContext();
  const [width, setWidth] = useState(
    Dimensions.get("window").width - ul.space.medium * 2 - ul.space.xsmall * 2
  );
  const height = 200;
  const paddingHorizxontal = ul.space.medium;

  const historyData = [0, 1, 2, 3, 4, 5, 6].map((weekday) => {
    const results = sections.flatMap((s) => ({
      color: s.color,
      val: s.results.find((r) => r.weekday === weekday)?.value ?? 0,
    }));

    return {
      weekday,
      results,
      total: results.reduce((a, b) => a + b.val, 0),
    };
  });

  const max = Math.max(...historyData.map((l) => l.total));
  const [format, _u] = formatValue(unit, max);
  const u = _u ?? "";
  const paddingVertical = 15;

  const barHeight = 15;
  const scaleY = d
    .scaleLinear()
    .domain([6, 0])
    .range([height - paddingVertical - barHeight, paddingVertical]);

  const dayTextWidth = 60;
  const valueTextWidth = 40;
  const scaleX = useMemo(
    () =>
      d
        .scaleLinear()
        .range([
          width - paddingHorizxontal - valueTextWidth,
          paddingHorizxontal + dayTextWidth,
        ])
        .domain([max, 0]),

    [max, paddingHorizxontal, width]
  );

  const theme = useTheme();

  const weekdays = useWeekdays("long");
  return (
    <Box spaceVertical="medium" spaceHorizontal="medium">
      <Box cornerRadius="medium" color="surface" spaceVertical="medium">
        <HeaderContainer>
          <AHText type="subtitle">
            <AHRoutineIcon size="xsmall" type={icon ?? "key"} /> {title ?? ""}
          </AHText>
          <Box>
            <AHText type="detail">Genomsnitt per veckodag</AHText>
            <AHText style={{ textAlign: "right" }} type="detail">
              Senaste 30 dagarna
            </AHText>
          </Box>
        </HeaderContainer>

        <View
          style={{ flex: 1, marginTop: 5 }}
          onLayout={(e) => {
            setWidth(e.nativeEvent.layout.width);
          }}
        >
          <Svg
            style={{ flex: 1 }}
            pointerEvents="none"
            width={width}
            height={height}
          >
            {scaleY.ticks(6).map((weekday) => {
              return (
                <Rect
                  key={weekday + ""}
                  width={scaleX(max) - scaleX(0)}
                  height={barHeight}
                  x={scaleX(0)}
                  fill={theme.background}
                  y={scaleY(weekday)}
                  rx={5}
                />
              );
            })}
            {historyData.map(({ total, results, weekday }, i) => {
              const barWidth = scaleX(total) - scaleX(0);
              let x = scaleX(0);
              return (
                <React.Fragment key={weekday + ""}>
                  {barWidth < 2 ? null : (
                    <>
                      <ClipPath id={weekday + ""}>
                        <Rect
                          width={barWidth}
                          height={barHeight}
                          x={scaleX(0)}
                          y={scaleY(weekday)}
                          rx={5}
                        />
                      </ClipPath>
                      <G clipPath={toClipPathUrl(weekday + "")}>
                        {results.map((r, i) => {
                          const result = (
                            <Rect
                              key={i + ""}
                              fill={r.color}
                              width={scaleX(r.val) - scaleX(0)}
                              height={barHeight}
                              x={x}
                              y={scaleY(weekday)}
                            />
                          );
                          x += scaleX(r.val) - scaleX(0);
                          return result;
                        })}
                      </G>
                    </>
                  )}

                  <Text
                    dy={scaleY(weekday) + barHeight / 2 + 3}
                    x={scaleX(max) + valueTextWidth}
                    fontFamily={AHTextFonts.MEDIUM as any}
                    fontSize={8}
                    textAnchor="end"
                    fill={theme.darkerText}
                  >
                    {format(total) + (u ?? "")}
                  </Text>
                </React.Fragment>
              );
            })}

            {scaleY.ticks(6).map((weekday, i) => {
              return (
                <Text
                  key={i + ""}
                  dy={scaleY(weekday) + barHeight / 2 + 3}
                  x={paddingHorizxontal}
                  fill={theme.darkerText}
                  fontFamily={AHTextFonts.BOLD as any}
                  fontSize={9}
                >
                  {weekdays[weekday]}
                </Text>
              );
            })}
          </Svg>
        </View>
      </Box>
    </Box>
  );
}

function HeaderContainer(props: PropsWithChildren<unknown>) {
  return (
    <Box spaceHorizontal="medium">
      <Box
        style={{
          height: 30,
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        {props.children}
      </Box>
      <AHSeparator />
    </Box>
  );
}
